import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  completeInternalDonationCheck,
  createInternalDonationCardIntent,
  confirmInternalDonationCardPayment,
} from '../../../endpoints/donations';
import { useApiSend } from '../../../utils/httpClient';
import { getApiErrorMessage } from '../../../utils/httpClientTs';
import { formatCurrency } from '../../../utils/formatters';
import {
  StorefrontStripePaymentSection,
  stripePromise,
} from '../../ChapterSuppliesStorefront/StorefrontStripePaymentForm';

/** API payment_method value is "bulk"; staff UI label is "ACH / Transmittal". */
const BULK_PAYMENT_METHOD = 'bulk';

export default function InternalDonationPayment({
  donationId,
  donationObject,
  handleBack,
}) {
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [checkNumber, setCheckNumber] = useState('');
  const [checkMemo, setCheckMemo] = useState('');
  const [error, setError] = useState(null);
  const [cardSession, setCardSession] = useState(null);

  const total = donationObject?.amount ?? 0;
  const lineCount = donationObject?.donation_lines?.length ?? 0;

  const { mutate: completeCheck, isPending: isCheckPending } = useApiSend(
    completeInternalDonationCheck,
    () => navigate(`/internal/donations/${donationId}`),
    (err) => setError(getApiErrorMessage(err))
  );

  const { mutate: startCardIntent, isPending: isCardIntentPending } =
    useApiSend(
      createInternalDonationCardIntent,
      (data) => {
        setCardSession({
          order_id: donationId,
          payment_intent_id: data.payment_intent_id,
          payment_client_secret: data.client_secret,
        });
        setError(null);
      },
      (err) => setError(getApiErrorMessage(err))
    );

  const handleCompleteCheck = () => {
    setError(null);
    const num = checkNumber.trim();
    if (!num) {
      setError('Check or reference number is required.');
      return;
    }
    if (lineCount < 1 || total <= 0) {
      setError('Add at least one donation line before completing payment.');
      return;
    }
    completeCheck({
      id: donationId,
      payment_method: paymentMethod,
      check_number: num,
      check_memo: checkMemo.trim() || undefined,
    });
  };

  const handlePrepareCard = () => {
    setError(null);
    setCardSession(null);
    if (lineCount < 1 || total <= 0) {
      setError('Add at least one donation line before taking card payment.');
      return;
    }
    startCardIntent({ id: donationId });
  };

  useEffect(() => {
    setCardSession(null);
  }, [paymentMethod]);

  const canPay = lineCount >= 1 && total > 0;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        Payment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Total: {formatCurrency(total)}
      </Typography>

      <ToggleButtonGroup
        exclusive
        value={paymentMethod}
        onChange={(_, v) => v && setPaymentMethod(v)}
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="check">Check</ToggleButton>
        <ToggleButton value="cash">Cash</ToggleButton>
        <ToggleButton value="card">Card</ToggleButton>
        <ToggleButton value={BULK_PAYMENT_METHOD}>
          ACH / Transmittal
        </ToggleButton>
      </ToggleButtonGroup>

      {paymentMethod === BULK_PAYMENT_METHOD && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Transactions not tracked by the system that are part of other
              untracked payment methods (for example ACH or transmittal). Not
              written to check batches. Acknowledgement workflow will be set to
              pending.
            </Typography>
            <Button
              variant="contained"
              disabled={!canPay || isCheckPending}
              onClick={() => {
                setError(null);
                if (lineCount < 1 || total <= 0) {
                  setError(
                    'Add at least one donation line before completing payment.'
                  );
                  return;
                }
                completeCheck({
                  id: donationId,
                  payment_method: BULK_PAYMENT_METHOD,
                  check_number: '',
                });
              }}
            >
              {isCheckPending ? 'Saving…' : 'Complete donation'}
            </Button>
          </CardContent>
        </Card>
      )}

      {(paymentMethod === 'check' || paymentMethod === 'cash') && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <TextField
                label={
                  paymentMethod === 'cash' ? 'Reference number' : 'Check number'
                }
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                size="small"
                fullWidth
                required
                helperText={
                  paymentMethod === 'cash'
                    ? 'e.g. CASH or receipt number (flows through check batches).'
                    : undefined
                }
              />
              <TextField
                label="Memo (optional)"
                value={checkMemo}
                onChange={(e) => setCheckMemo(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={2}
              />
              <Button
                variant="contained"
                disabled={!canPay || isCheckPending}
                onClick={handleCompleteCheck}
              >
                {isCheckPending ? 'Saving…' : 'Complete donation'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {paymentMethod === 'card' && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            {!stripePromise && (
              <Alert severity="error">
                Stripe is not configured for card payments.
              </Alert>
            )}
            {!cardSession && (
              <Button
                variant="contained"
                disabled={!canPay || !stripePromise || isCardIntentPending}
                onClick={handlePrepareCard}
              >
                {isCardIntentPending ? 'Preparing…' : 'Continue to card entry'}
              </Button>
            )}
            {cardSession && stripePromise && (
              <StorefrontStripePaymentSection
                checkoutSession={cardSession}
                submitLabel="Complete donation"
                confirmPayment={async ({ payment_intent_id }) => {
                  await confirmInternalDonationCardPayment({
                    id: donationId,
                    payment_intent_id,
                  });
                  return {};
                }}
                onError={(msg) => setError(msg)}
                onSuccess={() => navigate(`/internal/donations/${donationId}`)}
              />
            )}
          </CardContent>
        </Card>
      )}

      <Stack direction="row" spacing={2}>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
        <Button variant="text" onClick={() => navigate('/internal/donations')}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
}
